import { classNames } from "@ct-react/core";
import { IconX } from "@tabler/icons-react";
import { card } from "../components/article-card";
import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

type WishlistItem = { id: string | number; [k: string]: any };

type WishlistContextValue = {
  wishlist: WishlistItem[];
  has: (id: WishlistItem["id"]) => boolean;
  toggle: (item: WishlistItem) => { added: boolean };
  removeById: (id: WishlistItem["id"]) => void;
  popup: () => ReactNode,
  countItem: () => number | null
};

const WishlistContext = createContext<WishlistContextValue | null>(null);

export const WishlistProvider = ({ children }: { children: React.ReactNode }) => {
  const [wishlist, setWishlist] = useState<WishlistItem[]>([]);
  const [state, setState] = useState<"added" | "removed" | null>(null);
  const intl = useIntl();

  useEffect(() => {
    const raw = localStorage.getItem("WishList");
    setWishlist(raw ? JSON.parse(raw) : []);
  }, []);

  const value = useMemo<WishlistContextValue>(() => {
    const has = (id: WishlistItem["id"]) => wishlist.some((i) => i.id === id);

    const toggle = (item: WishlistItem) => {
      let added = false;

      setWishlist((prev) => {
        const exists = prev.some((i) => i.id === item.id);
        const next = exists ? prev.filter((i) => i.id !== item.id) : [...prev, item];
        added = !exists;
        added ? setState("added") : setState("removed");
        localStorage.setItem("WishList", JSON.stringify(next));

        return next;
      });

      return { added };
    };

    const removeById = (id: number | string) => {
      setWishlist((prev) => {
        const next = prev.filter((i: any) => i.id !== id);
        localStorage.setItem("WishList", JSON.stringify(next));
        setState("removed")
        return next;
      });
    };

    const popup = () => {
      if(!!state){
        setTimeout(() => {setState(null)}, 1500)
        return(
          <div className={classNames("pop-up",{opened: !!state})}>
            <button type="button" className="close" onClick={() => setState(null)}>
              <IconX/>
            </button>
            {state === "added" ?
            <>
              <h3>{intl.formatMessage(card.added_wishlist_title)}</h3>
              <p>{intl.formatMessage(card.added_wishlist)}</p>
            </>
            :
            <>
              <h3>{intl.formatMessage(card.removed_wishlist_title)}</h3>
              <p>{intl.formatMessage(card.removed_wishlist)}</p>
            </>
            }
          </div>
        );
      }
    };

    const countItem = () => {
      const count =  wishlist.length;
      if(count > 0){
        return count;
      }else{
        return null;
      }
    };

    return { wishlist, has, toggle, removeById, popup, countItem };
  }, [wishlist,state]);

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error("useWishlist must be used within WishlistProvider");
  return ctx;
}
